import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { Container, useTick } from '@pixi/react';
import { useMemo } from 'react';
import { useCallback, useRef } from 'react';
import { loadedBombSpriteSheets } from '../../../bombs/bombLoaderUtils';
import { removeBombSplat } from '../../../redux/actions/bombsActions';
import { useAppDispatch } from '../../../redux/store';
import { ALL_BOMB_PROPERTIES } from '../../../types/bombTypes';
import { isTestEnvironment } from '../../../utils/utils';
import { InternalAnimatedSprite } from '../pixiComponents/internalAnimatedSprite';
const SPLAT_DURATION_MS = 8000;
const SPLAT_FADE_OUT_DURATION_MS = 1500;
export const BombSplatDisplay = ({ bombSplat, baseBombScaleFactor }) => {
    const dispatch = useAppDispatch();
    const containerRef = useRef(null);
    const animate = useCallback(() => {
        if (!containerRef.current) {
            return;
        }
        const { id, startTimestampMs, seatIndex } = bombSplat;
        const nowMs = performance.now();
        if (nowMs < startTimestampMs + SPLAT_DURATION_MS) {
            // fade out hasn't started yet
            return;
        }
        const fadeOutElapsedMs = nowMs - (startTimestampMs + SPLAT_DURATION_MS);
        const remainingFadeOutRatio = Math.max(0, SPLAT_FADE_OUT_DURATION_MS - fadeOutElapsedMs) / SPLAT_FADE_OUT_DURATION_MS;
        if (remainingFadeOutRatio <= 0) {
            // fade out is done, remove bomb splat entirely:
            dispatch(removeBombSplat(seatIndex, id));
        }
        else {
            containerRef.current.alpha = remainingFadeOutRatio;
        }
    }, [bombSplat, dispatch]);
    useTick(animate);
    const radians = useMemo(() => {
        const { radiansRange } = ALL_BOMB_PROPERTIES[bombSplat.bombType].bombSplatProperties;
        if (radiansRange === undefined) {
            return bombSplat.radians;
        }
        return bombSplat.radians + (Math.random() - 0.5) * 2 * Math.PI * radiansRange;
    }, [bombSplat.bombType, bombSplat.radians]);
    const textures = useMemo(() => {
        const bombSpritesheets = loadedBombSpriteSheets[bombSplat.bombType];
        if (!bombSpritesheets) {
            return;
        }
        const animations = bombSpritesheets.bombSplat.animations;
        if (animations && Object.keys(animations).length) {
            return animations[Object.keys(animations)[0]];
        }
    }, [bombSplat.bombType]);
    if (!textures) {
        return null;
    }
    const pixiData = isTestEnvironment()
        ? {
            type: 'bomb-splat',
            bombType: bombSplat.bombType,
            id: bombSplat.id,
            seatIndex: bombSplat.seatIndex
        }
        : null;
    const { scaleFactor } = ALL_BOMB_PROPERTIES[bombSplat.bombType].bombSplatProperties;
    const scale = baseBombScaleFactor * scaleFactor;
    return (_jsx(Container, { ref: containerRef, x: 0, y: 0, rotation: radians, "pixi-data": pixiData, children: _jsx(Container, { scale: scale, children: _jsx(InternalAnimatedSprite, { textures: textures, animationSpeed: 0.5, loop: false }) }) }));
};
